import { AppError } from '@aibi/shared';
import { Badge, Button, Card, cn, Field, Input, PasswordInput, Select, Spinner } from '@aibi/ui';
import {
  ArrowLeft,
  CheckCircle2,
  Database,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Server,
  Upload,
  Warehouse,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { fieldErrorsOf } from '../../api/http-client';
import {
  isRemoteInput,
  type Connection,
  type ConnectionInput,
  type ConnectionTest,
  type ConnectionType,
  type ImportSource,
  type StoredFile,
} from '../services/admin-api';
import { useAdminServices } from '../state/admin-session';
import { formatBytes, SSL_MODES, STATUS_LABEL } from './connection-labels';
import { ErrorNotice, Notice } from './ErrorNotice';
import {
  databricksErrors,
  mysqlErrors,
  normalizeDbxHost,
  normalizeDbxPath,
  normalizeSqlHost,
  pgErrors,
  sqlServerErrors,
  type DatabricksForm,
  type MysqlForm,
  type PgForm,
  type SqlServerForm,
} from './form-rules';
import {
  emptyMysql,
  emptySqlServer,
  mysqlSecretRequired,
  sqlServerSecretRequired,
} from './sql-connection-forms';
import { MysqlFields, SqlServerFields } from './SqlConnectionFields';

export interface ConnectionWizardProps {
  readonly types: readonly ConnectionType[];
  readonly importEnabled: boolean;
  /** Conexion a editar; sin ella se crea una nueva. */
  readonly editing?: Connection;
  readonly onCancel: () => void;
  readonly onSaved: (connection: Connection) => void;
}

type TestState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'testing' }
  | { readonly phase: 'ok'; readonly result: ConnectionTest }
  | { readonly phase: 'error'; readonly error: AppError };

const TYPE_ICONS: Readonly<Record<string, ReactNode>> = {
  csv: <FileText />,
  parquet: <FileSpreadsheet />,
  postgresql: <Database />,
  databricks: <Warehouse />,
  sqlserver: <Server />,
  mysql: <Database />,
  excel: <FileSpreadsheet />,
};

const TYPE_DESCRIPTIONS: Readonly<Record<string, string>> = {
  csv: 'Archivo .csv, .txt o .tsv separado por comas, punto y coma, tabuladores o barras verticales (|). El formato se detecta solo.',
  parquet: 'Archivo columnar exportado desde herramientas de datos.',
  postgresql: 'Base de datos de la empresa. Use un usuario de solo lectura.',
  databricks:
    'Lakehouse con Unity Catalog a traves de un SQL Warehouse. Use un service principal de solo lectura.',
  sqlserver:
    'SQL Server o Azure SQL con usuario SQL o service principal de Entra ID. Use un usuario db_datareader.',
  mysql: 'MySQL, MariaDB o Azure Database for MySQL. Use un usuario con solo SELECT.',
  excel: 'Libro .xlsx para revisar su contenido antes de convertirlo.',
};

const emptyDbx = (editing?: Connection): DatabricksForm => ({
  host: editing?.host ?? '',
  httpPath: editing?.httpPath ?? '',
  catalog: editing?.catalog ?? '',
  schema: editing?.schema ?? '',
  authType: editing?.authType === 'oauth-m2m' ? 'oauth-m2m' : 'pat',
  clientId: editing?.clientId ?? '',
  secret: '',
});

/** Igual que el servidor: el secreto guardado solo sirve si no cambian servidor ni identidad. */
function dbxSecretRequired(form: DatabricksForm, editing: Connection | undefined): boolean {
  if (editing?.connectorId !== 'databricks' || !editing.hasPassword) return true;
  const clientId = form.authType === 'oauth-m2m' ? form.clientId.trim() : '';
  return (
    normalizeDbxHost(form.host) !== editing.host ||
    form.authType !== editing.authType ||
    clientId !== (editing.clientId ?? '')
  );
}

const emptyPg = (editing?: Connection): PgForm => ({
  host: editing?.host ?? '',
  port: String(editing?.port ?? 5432),
  database: editing?.database ?? '',
  schema: editing?.schema ?? '',
  user: editing?.user ?? '',
  password: '',
  sslMode: SSL_MODES.find((mode) => mode.value === editing?.sslMode)?.value ?? 'prefer',
});

/**
 * Alta o edicion de una conexion en pasos: tipo, datos, prueba y guardado. La contrasena
 * solo vive en el estado del formulario y se vacia al guardar.
 */
export function ConnectionWizard({
  types,
  importEnabled,
  editing,
  onCancel,
  onSaved,
}: ConnectionWizardProps) {
  const { admin } = useAdminServices();
  const [typeId, setTypeId] = useState<string | null>(editing?.connectorId ?? null);
  const type = types.find((candidate) => candidate.id === typeId) ?? null;
  const [name, setName] = useState(editing?.name ?? '');
  const [file, setFile] = useState<StoredFile | null>(null);
  const [pg, setPg] = useState<PgForm>(() => emptyPg(editing));
  const [dbx, setDbx] = useState<DatabricksForm>(() => emptyDbx(editing));
  const [mssql, setMssql] = useState<SqlServerForm>(() => emptySqlServer(editing));
  const [mysql, setMysql] = useState<MysqlForm>(() => emptyMysql(editing));
  const [touched, setTouched] = useState(false);
  const [test, setTest] = useState<TestState>({ phase: 'idle' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<AppError | null>(null);

  const isFile = type?.kind === 'file';
  const locked = (editing?.models.length ?? 0) > 0;
  const nameError =
    name.trim() === '' ? 'Ponga un nombre que reconozca, por ejemplo "Ventas 2026".' : null;
  const fileError = isFile && !file && !editing ? 'Elija o importe un archivo.' : null;
  const secretRequired =
    type?.id === 'sqlserver'
      ? sqlServerSecretRequired(mssql, editing)
      : type?.id === 'mysql'
        ? mysqlSecretRequired(mysql, editing)
        : dbxSecretRequired(dbx, editing);
  const remoteFieldErrors: Partial<Record<string, string>> =
    type?.id === 'postgresql'
      ? pgErrors(pg)
      : type?.id === 'databricks'
        ? databricksErrors(dbx, secretRequired)
        : type?.id === 'sqlserver'
          ? sqlServerErrors(mssql, secretRequired)
          : type?.id === 'mysql'
            ? mysqlErrors(mysql, secretRequired)
            : {};
  const serverFieldErrors = saveError ? fieldErrorsOf(saveError) : {};
  const valid = !nameError && !fileError && Object.keys(remoteFieldErrors).length === 0;
  const visibleErrors = touched
    ? { ...serverFieldErrors, ...remoteFieldErrors }
    : serverFieldErrors;

  const resetTest = () => {
    setTest({ phase: 'idle' });
    setSaveError(null);
  };

  const buildInput = (): ConnectionInput | null => {
    if (type === null) return null;
    if (type.id === 'postgresql') {
      return {
        connectorId: 'postgresql',
        name: name.trim(),
        host: pg.host.trim(),
        port: Number(pg.port),
        database: pg.database.trim(),
        ...(pg.schema.trim() === '' ? {} : { schema: pg.schema.trim() }),
        user: pg.user.trim(),
        ...(pg.password === '' ? {} : { password: pg.password }),
        sslMode: pg.sslMode,
      };
    }
    if (type.id === 'databricks') {
      const optional = (key: 'catalog' | 'schema' | 'clientId', value: string) =>
        value.trim() === '' ? {} : { [key]: value.trim() };
      return {
        connectorId: 'databricks',
        name: name.trim(),
        host: normalizeDbxHost(dbx.host),
        httpPath: normalizeDbxPath(dbx.httpPath),
        ...optional('catalog', dbx.catalog),
        ...optional('schema', dbx.schema),
        authType: dbx.authType,
        ...(dbx.authType === 'oauth-m2m' ? optional('clientId', dbx.clientId) : {}),
        ...(dbx.secret === '' ? {} : { secret: dbx.secret }),
      };
    }
    if (type.id === 'sqlserver') {
      const sp = mssql.authType === 'entra-sp';
      return {
        connectorId: 'sqlserver',
        name: name.trim(),
        host: normalizeSqlHost(mssql.host),
        port: Number(mssql.port),
        database: mssql.database.trim(),
        ...(mssql.schema.trim() === '' ? {} : { schema: mssql.schema.trim() }),
        authType: mssql.authType,
        ...(sp
          ? { tenantId: mssql.tenantId.trim(), clientId: mssql.clientId.trim() }
          : { user: mssql.user.trim() }),
        encrypt: mssql.encrypt,
        trustServerCertificate: mssql.trustServerCertificate,
        ...(mssql.secret === '' ? {} : { secret: mssql.secret }),
      };
    }
    if (type.id === 'mysql') {
      return {
        connectorId: 'mysql',
        name: name.trim(),
        host: normalizeSqlHost(mysql.host),
        port: Number(mysql.port),
        database: mysql.database.trim(),
        user: mysql.user.trim(),
        ...(mysql.password === '' ? {} : { password: mysql.password }),
        sslMode: mysql.sslMode,
      };
    }
    return {
      connectorId: type.id as 'csv' | 'parquet' | 'excel',
      name: name.trim(),
      ...(file ? { fileId: file.fileId } : {}),
    };
  };

  const runTest = async () => {
    setTouched(true);
    const input = buildInput();
    if (!valid || input === null) return;
    setTest({ phase: 'testing' });
    setSaveError(null);
    if (editing && !file && isFile) {
      const saved = await admin.testSaved(editing.id);
      if (!saved.ok) setTest({ phase: 'error', error: saved.error });
      else if (saved.value.status === 'connected') {
        setTest({
          phase: 'ok',
          result: { ok: true, latencyMs: 0, warnings: saved.value.warnings, objects: 0 },
        });
      } else {
        setTest({
          phase: 'error',
          error: new AppError('UNAVAILABLE', saved.value.message ?? 'La conexion no respondio.'),
        });
      }
      return;
    }
    const result = await admin.testDraft(
      editing && isRemoteInput(input) ? { ...input, connectionId: editing.id } : input,
    );
    setTest(
      result.ok ? { phase: 'ok', result: result.value } : { phase: 'error', error: result.error },
    );
  };

  const save = async () => {
    setTouched(true);
    const input = buildInput();
    if (!valid || input === null || saving) return;
    setSaving(true);
    setSaveError(null);
    const result = editing ? await admin.update(editing.id, input) : await admin.create(input);
    setSaving(false);
    setPg((current) => ({ ...current, password: '' }));
    setDbx((current) => ({ ...current, secret: '' }));
    setMssql((current) => ({ ...current, secret: '' }));
    setMysql((current) => ({ ...current, password: '' }));
    if (result.ok) onSaved(result.value);
    else setSaveError(result.error);
  };

  const step = type === null ? 1 : test.phase === 'ok' ? 3 : 2;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          <ArrowLeft /> Conexiones
        </Button>
        <h2 className="text-lg font-semibold">
          {editing ? `Editar "${editing.name}"` : 'Nueva conexion'}
        </h2>
      </div>
      <Stepper current={step} steps={['Tipo de fuente', 'Datos de conexion', 'Probar y guardar']} />

      {type === null ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {types.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              className="flex gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
              onClick={() => {
                setTypeId(candidate.id);
              }}
            >
              <span className="mt-0.5 text-primary [&_svg]:size-5">
                {TYPE_ICONS[candidate.id] ?? <FileText />}
              </span>
              <span className="flex flex-col gap-1">
                <span className="flex items-center gap-2 text-sm font-medium">
                  {candidate.label}
                  {!candidate.query && <Badge variant="warning">Solo exploracion</Badge>}
                </span>
                <span className="text-xs text-muted-foreground">
                  {TYPE_DESCRIPTIONS[candidate.id] ?? ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <Card className="gap-4 p-5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className="text-primary [&_svg]:size-4">{TYPE_ICONS[type.id]}</span>
              {type.label}
              {!type.query && <Badge variant="warning">Solo exploracion</Badge>}
            </div>
            {!editing && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setTypeId(null);
                  setFile(null);
                  resetTest();
                }}
              >
                Cambiar tipo
              </Button>
            )}
          </div>
          {type.note && <Notice tone="warning">{type.note}</Notice>}
          {locked && (
            <Notice tone="info">
              Esta conexion alimenta modelos publicados: solo puede cambiar el nombre y la
              credencial. Para cambiar el servidor, la base de datos o el archivo, retire antes los
              modelos desde la lista de conexiones (boton Retirar).
            </Notice>
          )}

          <Field
            label="Nombre de la conexion"
            error={(touched ? nameError : null) ?? serverFieldErrors.name}
          >
            {(control) => (
              <Input
                {...control}
                value={name}
                maxLength={120}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            )}
          </Field>

          {isFile && locked ? (
            <p className="text-xs text-muted-foreground">
              Archivo: {editing?.fileName ?? 'Archivo'}
            </p>
          ) : isFile ? (
            <FileSource
              type={type}
              importEnabled={importEnabled}
              current={file}
              existingName={editing?.fileName ?? null}
              error={touched ? fileError : null}
              onFile={(stored) => {
                setFile(stored);
                if (name.trim() === '') setName(stored.fileName.replace(/\.[^.]+$/, ''));
                resetTest();
              }}
            />
          ) : type.id === 'databricks' ? (
            <DatabricksFields
              form={dbx}
              errors={visibleErrors}
              savedSecretUsable={!secretRequired}
              locked={locked}
              onChange={(next) => {
                setDbx(next);
                resetTest();
              }}
            />
          ) : type.id === 'sqlserver' ? (
            <SqlServerFields
              form={mssql}
              errors={visibleErrors}
              savedSecretUsable={!secretRequired}
              locked={locked}
              onChange={(next) => {
                setMssql(next);
                resetTest();
              }}
            />
          ) : type.id === 'mysql' ? (
            <MysqlFields
              form={mysql}
              errors={visibleErrors}
              savedSecretUsable={!secretRequired}
              locked={locked}
              onChange={(next) => {
                setMysql(next);
                resetTest();
              }}
            />
          ) : (
            <PgFields
              form={pg}
              errors={visibleErrors}
              hasSavedPassword={editing?.hasPassword ?? false}
              locked={locked}
              onChange={(next) => {
                setPg(next);
                resetTest();
              }}
            />
          )}

          <TestPanel test={test} />
          {saveError && <ErrorNotice error={saveError} />}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={test.phase === 'testing'}
              onClick={() => void runTest()}
            >
              {test.phase === 'testing' ? 'Probando...' : 'Probar conexion'}
            </Button>
            <Button size="sm" disabled={test.phase !== 'ok' || saving} onClick={() => void save()}>
              {saving ? 'Guardando...' : editing ? 'Guardar cambios' : 'Guardar conexion'}
            </Button>
            {test.phase !== 'ok' && (
              <span className="text-xs text-muted-foreground">
                Pruebe la conexion antes de guardarla.
              </span>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

function Stepper({
  current,
  steps,
}: {
  readonly current: number;
  readonly steps: readonly string[];
}) {
  return (
    <ol className="flex flex-wrap items-center gap-2 text-xs" aria-label="Pasos">
      {steps.map((label, index) => {
        const number = index + 1;
        const state = number < current ? 'done' : number === current ? 'current' : 'next';
        return (
          <li
            key={label}
            aria-current={state === 'current' ? 'step' : undefined}
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-2.5 py-1',
              state === 'current' && 'border-primary bg-primary/10 text-primary',
              state === 'done' && 'border-success/40 text-success',
              state === 'next' && 'border-border text-muted-foreground',
            )}
          >
            {state === 'done' ? (
              <CheckCircle2 aria-hidden className="size-3.5" />
            ) : (
              <span>{number}.</span>
            )}
            {label}
          </li>
        );
      })}
    </ol>
  );
}

function TestPanel({ test }: { readonly test: TestState }) {
  if (test.phase === 'idle') return null;
  if (test.phase === 'testing') {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
        <Spinner label="Conectando" /> {STATUS_LABEL.connecting.label}
      </div>
    );
  }
  if (test.phase === 'error') return <ErrorNotice error={test.error} />;
  const { result } = test;
  return (
    <div className="flex flex-col gap-2">
      <Notice tone="info">
        <p className="font-medium text-success">Conexion correcta</p>
        {result.objects > 0 && (
          <p>
            {result.objects === 1
              ? 'Se encontro 1 tabla'
              : `Se encontraron ${String(result.objects)} tablas`}
            {result.latencyMs > 0 ? ` en ${String(result.latencyMs)} ms.` : '.'}
          </p>
        )}
      </Notice>
      {result.warnings.map((warning) => (
        <Notice key={warning} tone="warning">
          {warning}
        </Notice>
      ))}
    </div>
  );
}

function PgFields({
  form,
  errors,
  hasSavedPassword,
  locked,
  onChange,
}: {
  readonly form: PgForm;
  readonly errors: Partial<Record<string, string>>;
  readonly hasSavedPassword: boolean;
  readonly locked: boolean;
  readonly onChange: (form: PgForm) => void;
}) {
  const set = (key: keyof PgForm) => (event: { target: { value: string } }) => {
    onChange({ ...form, [key]: event.target.value });
  };
  return (
    <div className="flex flex-col gap-3">
      <fieldset disabled={locked} className="min-w-0">
        <legend className="sr-only">Datos del servidor</legend>
        <PgServerFields form={form} errors={errors} set={set} />
      </fieldset>
      <Field
        label="Contrasena"
        error={errors.password}
        hint={
          hasSavedPassword
            ? 'Guardada. Dejela vacia para conservarla.'
            : 'Se guarda cifrada en el servidor.'
        }
      >
        {(control) => (
          <PasswordInput
            {...control}
            value={form.password}
            placeholder={hasSavedPassword ? '********' : ''}
            onChange={set('password')}
          />
        )}
      </Field>
    </div>
  );
}

function PgServerFields({
  form,
  errors,
  set,
}: {
  readonly form: PgForm;
  readonly errors: Partial<Record<string, string>>;
  readonly set: (key: keyof PgForm) => (event: { target: { value: string } }) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-6">
      <Field
        label="Servidor"
        className="sm:col-span-4"
        error={errors.host}
        hint="Nombre o IP, por ejemplo db.empresa.local"
      >
        {(control) => (
          <Input
            {...control}
            value={form.host}
            autoComplete="off"
            spellCheck={false}
            onChange={set('host')}
          />
        )}
      </Field>
      <Field label="Puerto" className="sm:col-span-2" error={errors.port}>
        {(control) => (
          <Input {...control} value={form.port} inputMode="numeric" onChange={set('port')} />
        )}
      </Field>
      <Field label="Base de datos" className="sm:col-span-3" error={errors.database}>
        {(control) => (
          <Input {...control} value={form.database} autoComplete="off" onChange={set('database')} />
        )}
      </Field>
      <Field
        label="Esquema (opcional)"
        className="sm:col-span-3"
        error={errors.schema}
        hint="Vacio para explorar todos los esquemas visibles."
      >
        {(control) => (
          <Input
            {...control}
            value={form.schema}
            placeholder="public"
            autoComplete="off"
            onChange={set('schema')}
          />
        )}
      </Field>
      <Field label="Usuario" className="sm:col-span-3" error={errors.user}>
        {(control) => (
          <Input
            {...control}
            value={form.user}
            autoComplete="off"
            spellCheck={false}
            onChange={set('user')}
          />
        )}
      </Field>
      <Field label="Cifrado de la conexion" className="sm:col-span-3" error={errors.sslMode}>
        {(control) => (
          <Select {...control} value={form.sslMode} onChange={set('sslMode')}>
            {SSL_MODES.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </div>
  );
}

const DBX_AUTH_OPTIONS = [
  { value: 'pat', label: 'Token de acceso personal' },
  { value: 'oauth-m2m', label: 'Service principal (OAuth M2M)' },
] as const;

function DatabricksFields({
  form,
  errors,
  savedSecretUsable,
  locked,
  onChange,
}: {
  readonly form: DatabricksForm;
  readonly errors: Partial<Record<string, string>>;
  /** Hay un secreto guardado que el servidor reutilizara si se deja vacio. */
  readonly savedSecretUsable: boolean;
  readonly locked: boolean;
  readonly onChange: (form: DatabricksForm) => void;
}) {
  const set = (key: keyof DatabricksForm) => (event: { target: { value: string } }) => {
    onChange({ ...form, [key]: event.target.value });
  };
  const isPat = form.authType === 'pat';
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="info">
        Databricks no ofrece sesiones de solo lectura: use un usuario o service principal que solo
        tenga USE CATALOG, USE SCHEMA y SELECT sobre los datos a analizar.
      </Notice>
      <fieldset disabled={locked} className="min-w-0">
        <legend className="sr-only">Datos del SQL Warehouse</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Server hostname"
            error={errors.host}
            hint="En Detalles de conexion del SQL Warehouse, por ejemplo adb-123.4.azuredatabricks.net"
          >
            {(control) => (
              <Input
                {...control}
                value={form.host}
                autoComplete="off"
                spellCheck={false}
                onChange={set('host')}
              />
            )}
          </Field>
          <Field label="HTTP Path" error={errors.httpPath} hint="/sql/1.0/warehouses/<id>">
            {(control) => (
              <Input
                {...control}
                value={form.httpPath}
                autoComplete="off"
                spellCheck={false}
                onChange={set('httpPath')}
              />
            )}
          </Field>
          <Field
            label="Catalogo (opcional)"
            error={errors.catalog}
            hint="Vacio: el catalogo por defecto del workspace."
          >
            {(control) => (
              <Input
                {...control}
                value={form.catalog}
                placeholder="main"
                autoComplete="off"
                onChange={set('catalog')}
              />
            )}
          </Field>
          <Field
            label="Esquema (opcional)"
            error={errors.schema}
            hint="Vacio para explorar todos los esquemas del catalogo."
          >
            {(control) => (
              <Input {...control} value={form.schema} autoComplete="off" onChange={set('schema')} />
            )}
          </Field>
        </div>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Autenticacion" error={errors.authType}>
          {(control) => (
            <Select {...control} value={form.authType} onChange={set('authType')}>
              {DBX_AUTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {!isPat && (
          <Field
            label="Client ID"
            error={errors.clientId}
            hint="Application ID del service principal."
          >
            {(control) => (
              <Input
                {...control}
                value={form.clientId}
                autoComplete="off"
                spellCheck={false}
                onChange={set('clientId')}
              />
            )}
          </Field>
        )}
      </div>
      <Field
        label={isPat ? 'Token de acceso' : 'Secreto del service principal'}
        error={errors.secret}
        hint={
          savedSecretUsable
            ? 'Guardado. Dejelo vacio para conservarlo.'
            : 'Se guarda cifrado en el servidor y nunca se vuelve a mostrar.'
        }
      >
        {(control) => (
          <PasswordInput
            {...control}
            value={form.secret}
            placeholder={savedSecretUsable ? '********' : ''}
            onChange={set('secret')}
          />
        )}
      </Field>
    </div>
  );
}

function FileSource({
  type,
  importEnabled,
  current,
  existingName,
  error,
  onFile,
}: {
  readonly type: ConnectionType;
  readonly importEnabled: boolean;
  readonly current: StoredFile | null;
  readonly existingName: string | null;
  readonly error: string | null;
  readonly onFile: (file: StoredFile) => void;
}) {
  const { admin } = useAdminServices();
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<AppError | null>(null);
  const [dragging, setDragging] = useState(false);
  const [mode, setMode] = useState<'upload' | 'import'>('upload');
  const [localError, setLocalError] = useState<string | null>(null);
  const accept = type.extensions.map((extension) => `.${extension}`).join(',');

  const upload = async (selected: File) => {
    const extension = selected.name.split('.').pop()?.toLowerCase() ?? '';
    if (!type.extensions.includes(extension)) {
      setUploadError(null);
      setProgress(null);
      setLocalError(`Elija un archivo ${accept}.`);
      return;
    }
    setLocalError(null);
    setUploadError(null);
    setProgress(0);
    const result = await admin.upload(selected, setProgress);
    setProgress(null);
    if (result.ok) onFile(result.value);
    else setUploadError(result.error);
  };

  return (
    <div className="flex flex-col gap-2">
      {importEnabled && (
        <div className="flex gap-1 text-xs" role="tablist" aria-label="Origen del archivo">
          {(['upload', 'import'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={mode === value}
              className={cn(
                'rounded-md px-2.5 py-1',
                mode === value
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent',
              )}
              onClick={() => {
                setMode(value);
              }}
            >
              {value === 'upload' ? 'Subir desde este equipo' : 'Importar desde el servidor'}
            </button>
          ))}
        </div>
      )}

      {mode === 'upload' ? (
        <div
          className={cn(
            'flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 text-center text-sm',
            dragging ? 'border-primary bg-primary/5' : 'border-border',
          )}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => {
            setDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            const dropped = event.dataTransfer.files[0];
            if (dropped) void upload(dropped);
          }}
        >
          <Upload aria-hidden className="size-5 text-muted-foreground" />
          <p>
            Arrastre el archivo aqui o{' '}
            <button
              type="button"
              className="text-primary underline"
              onClick={() => inputRef.current?.click()}
            >
              elijalo
            </button>
            .
          </p>
          <p className="text-xs text-muted-foreground">Formatos admitidos: {accept}</p>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="sr-only"
            aria-label="Elegir archivo"
            onChange={(event) => {
              const selected = event.target.files?.[0];
              event.target.value = '';
              if (selected) void upload(selected);
            }}
          />
          {progress !== null && (
            <div className="w-full max-w-xs">
              <div
                role="progressbar"
                aria-label="Subiendo archivo"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress * 100)}
                className="h-1.5 overflow-hidden rounded-full bg-muted"
              >
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${String(Math.round(progress * 100))}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Subiendo... {Math.round(progress * 100)}%
              </p>
            </div>
          )}
        </div>
      ) : (
        <ImportList connectorId={type.id} onFile={onFile} />
      )}

      {current ? (
        <p className="flex items-center gap-1.5 text-xs text-success">
          <CheckCircle2 aria-hidden className="size-3.5" /> {current.fileName} (
          {formatBytes(current.sizeBytes)})
        </p>
      ) : existingName ? (
        <p className="text-xs text-muted-foreground">
          Archivo actual: {existingName}. Elija otro solo si quiere reemplazarlo.
        </p>
      ) : null}
      {(localError ?? error) && (
        <p className="text-[11px] text-destructive">{localError ?? error}</p>
      )}
      {uploadError && <ErrorNotice error={uploadError} />}
    </div>
  );
}

function ImportList({
  connectorId,
  onFile,
}: {
  readonly connectorId: string;
  readonly onFile: (file: StoredFile) => void;
}) {
  const { admin } = useAdminServices();
  const [sources, setSources] = useState<readonly ImportSource[] | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void admin.importSources().then((result) => {
      if (cancelled) return;
      if (result.ok)
        setSources(result.value.filter((source) => source.connectorId === connectorId));
      else setError(result.error);
    });
    return () => {
      cancelled = true;
    };
  }, [admin, connectorId]);

  if (error) return <ErrorNotice error={error} />;
  if (sources === null) return <Spinner label="Cargando archivos del servidor" />;
  if (sources.length === 0) {
    return (
      <Notice tone="info">
        No hay archivos de este tipo en las carpetas del servidor habilitadas.
      </Notice>
    );
  }
  return (
    <ul className="flex max-h-56 flex-col divide-y divide-border overflow-auto rounded-lg border border-border">
      {sources.map((source) => (
        <li key={source.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
          <span className="flex min-w-0 items-center gap-2">
            <FolderOpen aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{source.name}</span>
            <span className="text-xs text-muted-foreground">
              {source.folder} · {formatBytes(source.sizeBytes)}
            </span>
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={busyId !== null}
            onClick={() => {
              setBusyId(source.id);
              void admin.importFile(source.id).then((result) => {
                setBusyId(null);
                if (result.ok) onFile(result.value);
                else setError(result.error);
              });
            }}
          >
            {busyId === source.id ? 'Importando...' : 'Usar'}
          </Button>
        </li>
      ))}
    </ul>
  );
}
